import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, AlertCircle, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { LogoIcon } from "@/components/Logo";

const STATUS = { LOADING: "l", READY: "r", DONE: "d", ERROR: "e" };

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState(STATUS.LOADING);
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then((r) => {
        setInv(r.data);
        setForm((f) => ({ ...f, name: r.data.name || "" }));
        if (r.data.status !== "pending") {
          setError(
            r.data.status === "accepted" ? "Esta invitación ya fue aceptada. Inicia sesión." :
            r.data.status === "revoked"  ? "Esta invitación fue revocada por el administrador." :
            r.data.status === "expired"  ? "Esta invitación ha caducado. Pide al admin que envíe una nueva." :
            "Esta invitación no es válida."
          );
          setStep(STATUS.ERROR);
        } else {
          setStep(STATUS.READY);
        }
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Invitación no encontrada");
        setStep(STATUS.ERROR);
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    // Validación cliente alineada con la política del backend (oración + 2
    // dígitos + 2 especiales + 20 chars). El backend valida de nuevo.
    const pw = form.password || "";
    const SPECIAL = /[!@#$%^&*()_+\-=[\]{};:'",.<>/?\\|~`]/g;
    const lenOk = pw.length >= 20;
    const spaceOk = (pw.match(/ /g) || []).length >= 2;
    const digitsOk = (pw.match(/\d/g) || []).length >= 2;
    const specialsOk = (pw.match(SPECIAL) || []).length >= 2;
    if (!(lenOk && spaceOk && digitsOk && specialsOk)) {
      toast.error("La contraseña debe ser una oración de 20+ caracteres con al menos 3 palabras, 2 números y 2 caracteres especiales.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/invitations/${token}/accept`, {
        name: form.name.trim(),
        password: form.password,
      });
      // Guarda sesión
      if (setSession) setSession(r.data.token, r.data.user);
      setStep(STATUS.DONE);
      toast.success("Cuenta creada · bienvenidx a Eksvidi");
      setTimeout(() => nav("/", { replace: true }), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la cuenta");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === STATUS.LOADING) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center text-[#7A786F]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando invitación…
      </div>
    );
  }

  if (step === STATUS.ERROR) {
    return (
      <Shell>
        <div className="text-center p-8" data-testid="invite-error">
          <AlertCircle className="h-12 w-12 text-[#A32D2D] mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Invitación no disponible</h1>
          <p className="text-[#7A786F] mt-2">{error}</p>
          <Button asChild className="btn-eks-primary rounded-[12px] mt-6" data-testid="go-login-btn">
            <a href="/login">Ir al inicio de sesión</a>
          </Button>
        </div>
      </Shell>
    );
  }

  if (step === STATUS.DONE) {
    return (
      <Shell>
        <div className="text-center p-8">
          <CheckCircle2 className="h-12 w-12 text-[#1D9E75] mx-auto mb-3" />
          <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">¡Bienvenidx a Eksvidi!</h1>
          <p className="text-[#7A786F] mt-2">Te estamos llevando al panel…</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="p-8" data-testid="invite-accept-form">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#7A786F]">
          <Mail className="h-3.5 w-3.5 text-[#D85A30]" /> Invitación de equipo
        </div>
        <h1 className="font-heading text-2xl font-bold text-[#2C2C2A] mt-1">
          Te ha invitado <span className="text-[#D85A30]">{inv.invited_by_name || "el administrador"}</span>
        </h1>
        <p className="text-sm text-[#7A786F] mt-1">
          Únete al equipo de <b className="text-[#2C2C2A]">{inv.org_name}</b> en Eksvidi como{" "}
          <b className="text-[#2C2C2A]">{inv.role === "admin" ? "administrador" : inv.role === "tramitador" ? "tramitador" : "perito"}</b>.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>Email</Label>
            <Input value={inv.email} disabled className="rounded-[12px] border-[#E5E2D8] bg-[#FBFAF6]" data-testid="invite-email-readonly" />
            <p className="text-[11px] text-[#7A786F] mt-1">
              Este email te identifica también en Ekspertium para vincular tu cuenta.
            </p>
          </div>
          <div>
            <Label htmlFor="name">Nombre completo *</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="María García"
              className="rounded-[12px] border-[#E5E2D8]"
              data-testid="invite-name-input"
            />
          </div>
          <div>
            <Label htmlFor="password">Contraseña *</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={20}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Una oración con números y símbolos…"
              className="rounded-[12px] border-[#E5E2D8]"
              data-testid="invite-password-input"
            />
            <p className="text-[11px] text-[#7A786F] mt-1 leading-snug">
              Tu contraseña debe ser una oración fácil de recordar, con al menos 20 caracteres, 3 palabras separadas por espacios, 2 números y 2 caracteres especiales (ej. <span className="font-mono">! @ # $ % &</span>).
            </p>
          </div>
          <Button type="submit" disabled={submitting} className="w-full btn-eks-primary rounded-[12px] h-11" data-testid="invite-accept-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Crear mi cuenta
          </Button>
          <p className="text-[11px] text-[#7A786F] text-center">
            Al continuar, aceptas el aviso de privacidad y los términos de Eksvidi.
          </p>
        </form>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FBFAF6] flex flex-col">
      <header className="px-6 py-5 flex items-center gap-2.5 border-b border-[#E5E2D8] bg-white">
        <LogoIcon size={32} />
        <div className="leading-tight">
          <div className="font-heading font-medium text-[16px] tracking-tight">
            <span className="text-[#2C2C2A]">eks</span>
            <span className="text-[#D85A30]">vidi</span>
          </div>
          <div className="text-[10px] text-[#7A786F] tracking-wider uppercase">by Ekspertium</div>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-[#E5E2D8] rounded-[18px] overflow-hidden">
          {children}
        </div>
      </main>
    </div>
  );
}
